import * as THREE from 'three';

import { ELEMENT_COLOR } from './palette.ts';
import { AssetRegistry } from './assets/registry.ts';
import { WAND_TIP, wandRole } from './assets/roles.ts';
import { SPELLS_BY_ID, chosenElement } from '../core/spells.ts';
import type { PlayerState } from '../core/player.ts';
import type { PlayerInput } from '../core/sim.ts';

/**
 * The hand and wand the local mage sees, and the light at its tip.
 *
 * Drawn in a scene of its own with a camera of its own, which is the whole trick
 * of a first-person viewmodel: the wand is only a foot from the eye, and in the
 * world scene it would clip through anything the player stood near and be sliced
 * by the near plane besides. A second pass with its own narrow field of view and
 * its own tiny depth range solves both, and costs one extra draw.
 *
 * Nothing here is authoritative. The tip is where a spell *appears* to come from;
 * the simulation fires from the eye, because a spell that genuinely left the
 * wand would leave at an angle to the crosshair and land beside what you aimed at.
 */

/** Narrower than the world's, so the wand does not distort at the screen edge. */
export const VIEWMODEL_FOV = 55;

/**
 * Where the hand is held, relative to the eye.
 *
 * Down, right, and a arm's length forward. The first version built the wand in
 * camera space and left the grip near the origin, which put a dark cuff five
 * centimetres from the lens — geometry that reached *behind* the near plane and
 * engulfed the screen rather than sitting in the corner of it. A viewmodel is
 * held out at a distance; that distance is this vector and nothing else should
 * try to encode it.
 */
export const HOLD = new THREE.Vector3(0.19, -0.15, -0.3);
/** Angled up and inward, so the tip sits nearer the crosshair than the fist. */
export const HOLD_PITCH = -0.06;
export const HOLD_YAW = 0.13;

/** How far the wand lags a turn, and how quickly it catches up. */
const SWAY_LIMIT = 0.055;
const SWAY_RECOVER = 7;
/** Depth of the walking bob, metres, and its rate against footfall. */
const BOB_AMOUNT = 0.011;
const BOB_SPEED = 9;
/** How far the wand kicks back on a cast, and how fast it returns. */
const CAST_KICK = 0.06;
const CAST_RECOVER = 6.5;

export class Viewmodel {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(VIEWMODEL_FOV, 1, 0.01, 5);

  private readonly rig = new THREE.Group();
  /** Carries the hold offset, so sway and bob move the rig and not the geometry. */
  private readonly hand = new THREE.Group();
  private wand: THREE.Object3D | null = null;
  private cls: string | null = null;

  private readonly tipGlow: THREE.Mesh;
  private readonly tipLight: THREE.PointLight;

  private sway = new THREE.Vector2();
  private bobPhase = 0;
  private kick = 0;
  private lastYaw = 0;
  private lastPitch = 0;

  private readonly assets: AssetRegistry;

  // Written out rather than declared as a constructor parameter property: Node's
  // strip-only TypeScript cannot parse those, so the tests could not import this
  // file at all while it used one.
  constructor(assets: AssetRegistry) {
    this.assets = assets;

    // Lit by its own small rig rather than by the arena, so the wand reads the
    // same in a torch-lit nave and a pitch-dark corridor. A viewmodel that
    // vanishes in the dark is a viewmodel the player stops trusting.
    this.scene.add(new THREE.AmbientLight(0x8a93a8, 1.1));
    const key = new THREE.DirectionalLight(0xffd9b0, 1.6);
    key.position.set(-0.6, 1, 0.4);
    this.scene.add(key);

    this.scene.add(this.rig);
    this.hand.position.copy(HOLD);
    this.hand.rotation.set(HOLD_PITCH, HOLD_YAW, 0);
    this.rig.add(this.hand);

    this.tipGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.028, 10, 8),
      new THREE.MeshBasicMaterial({
        color: 0xffffff, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }),
    );
    this.tipGlow.position.set(WAND_TIP.x, WAND_TIP.y, WAND_TIP.z);
    this.hand.add(this.tipGlow);

    this.tipLight = new THREE.PointLight(0xffffff, 0, 0.8, 2);
    this.tipLight.position.copy(this.tipGlow.position);
    this.hand.add(this.tipLight);
  }

  /**
   * Where a spell should appear to come from, in world space.
   *
   * Read off the glow itself rather than recomputed, so sway, bob and the cast
   * kick all carry it — and so it cannot drift from the thing the player can
   * actually see. The viewmodel camera sits at the origin looking down −Z, which
   * makes this scene's world space identical to camera space; that is the only
   * reason handing the result straight to the world camera works.
   */
  muzzleWorld(worldCamera: THREE.Camera): THREE.Vector3 {
    this.tipGlow.updateWorldMatrix(true, false);
    const inCameraSpace = this.tipGlow.getWorldPosition(new THREE.Vector3());
    return worldCamera.localToWorld(inCameraSpace);
  }

  applyAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Called when a cast actually lands, so the kick follows the spell. */
  punch(): void {
    this.kick = 1;
  }

  update(p: PlayerState, input: PlayerInput, dt: number): void {
    // A ghost has no hands, and nobody in Valhalla is casting anything.
    const visible = p.alive && !p.ghost && !p.inValhalla;
    this.rig.visible = visible;
    if (!visible) return;

    if (this.cls !== p.cls) {
      this.cls = p.cls;
      if (this.wand) this.hand.remove(this.wand);
      this.wand = this.assets.create({ role: wandRole(p.cls) });
      this.hand.add(this.wand);
    }

    // Sway: the wand lags the turn and catches up. Sampled from the change in
    // aim rather than from mouse deltas, so it behaves the same whatever the
    // sensitivity is set to.
    const dYaw = shortestAngle(input.yaw - this.lastYaw);
    const dPitch = input.pitch - this.lastPitch;
    this.lastYaw = input.yaw;
    this.lastPitch = input.pitch;

    this.sway.x = clamp(this.sway.x - dYaw * 0.35, -SWAY_LIMIT, SWAY_LIMIT);
    this.sway.y = clamp(this.sway.y - dPitch * 0.35, -SWAY_LIMIT, SWAY_LIMIT);
    this.sway.multiplyScalar(Math.max(0, 1 - SWAY_RECOVER * dt));

    // Bob, driven by what the player is doing rather than by how fast they are
    // actually moving — core does not integrate movement, and the two would
    // disagree the moment a wall slowed them down.
    const moving = Math.abs(input.moveForward) > 0.01 || Math.abs(input.moveRight) > 0.01;
    const rate = p.running ? 1.35 : p.crouching ? 0.5 : 1;
    if (moving && p.onGround) this.bobPhase += dt * BOB_SPEED * rate;
    const bobX = moving ? Math.sin(this.bobPhase) * BOB_AMOUNT * rate : 0;
    const bobY = moving ? Math.abs(Math.cos(this.bobPhase)) * BOB_AMOUNT * rate : 0;

    this.kick = Math.max(0, this.kick - CAST_RECOVER * dt);
    // Eased, so the recoil snaps out and settles back rather than sliding both ways.
    const kickEase = this.kick * this.kick;

    this.rig.position.set(
      this.sway.x + bobX,
      this.sway.y + bobY - kickEase * CAST_KICK * 0.35,
      kickEase * CAST_KICK,
    );
    this.rig.rotation.set(
      -this.sway.y * 2.2 + kickEase * 0.22,
      this.sway.x * 2.2,
      this.sway.x * 1.1,
    );

    this.updateTip(p);
  }

  /**
   * The tip carries the colour of what is readied and brightens as it becomes
   * castable, which is the same information the spell bar carries — but in the
   * middle of the screen, where a player already is.
   */
  private updateTip(p: PlayerState): void {
    const spell = SPELLS_BY_ID[p.loadout[p.selectedSlot] ?? ''];
    if (!spell) {
      this.tipLight.intensity = 0;
      (this.tipGlow.material as THREE.MeshBasicMaterial).opacity = 0;
      return;
    }

    const element = chosenElement(spell, p.slotElement[p.selectedSlot] ?? 0);
    const colour = ELEMENT_COLOR[element];
    const ready = p.cooldowns[p.selectedSlot] <= 0 && p.gcd <= 0;
    // Dim while it is coming back rather than dark, so the wand always reads as
    // holding something.
    const charge = ready ? 1 : 0.25;

    this.tipLight.color.setHex(colour);
    this.tipLight.intensity = 0.9 * charge + this.kick * 2.5;
    (this.tipGlow.material as THREE.MeshBasicMaterial).color.setHex(colour);
    (this.tipGlow.material as THREE.MeshBasicMaterial).opacity = 0.35 * charge + this.kick * 0.5;
    this.tipGlow.scale.setScalar(1 + this.kick * 0.9);
  }

  /** Drawn after the world, over the top, with the depth buffer cleared. */
  render(renderer: THREE.WebGLRenderer): void {
    if (!this.rig.visible) return;
    renderer.clearDepth();
    renderer.render(this.scene, this.camera);
  }
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Keeps a turn through north from reading as a full spin the other way. */
function shortestAngle(a: number): number {
  let out = a;
  while (out > Math.PI) out -= Math.PI * 2;
  while (out < -Math.PI) out += Math.PI * 2;
  return out;
}
